import React, { ChangeEvent, ReactElement, useState } from 'react';

import { formatProgrammeDates, getClassNames, isNumeric, validateSouthAfricanIdNumber } from '../../../../utils/util';

import useTheme from '../../../../hooks/use-theme/useTheme';

import { Field } from '../../../../types/common.types';
import { Programme } from '../../../../types/entity.types';

import AuthLayout from '../../../../layouts/auth/AuthLayout';
import Button from '../../../common/button/Button';
import Checkbox from '../../../common/input/checkbox/Checkbox';

import './programme-details.scss';

type Props = {
  programme?: Programme;
  fields: Field[];
  updateField: (label: string, value: string | boolean) => void;
  showToaster: (value: string) => void;
  onProceedToPaymentClick: () => void;
};

const ProgrammeDetails = ({
  programme,
  fields,
  updateField,
  showToaster,
  onProceedToPaymentClick
}: Props): ReactElement => {
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  // TODO: Fix after migrating theme hook
  const { pps } = useTheme();

  const handleContinueClick = (): void => {
    for (const field of fields) {
      if (!field.value && !field.optional) {
        showToaster('Please fill in all required fields');
        return;
      }

      if (field.label === 'ID Number') {
        const validId: boolean = validateSouthAfricanIdNumber(field.value);
        if (!validId) {
          showToaster('Please provide a valid South African ID Number');
          return;
        }
      }
    }

    setShowConfirmation(!showConfirmation);
  };

  const renderConfirmationValue = (field: Field): string => {
    if (field.type === 'checkbox') {
      return field.value ? 'Yes' : 'No';
    }

    return field.value || '-';
  };

  const handleFieldChange = (field: Field, value: string): void => {
    if (field.label === 'ID Number' || field.label === 'Mobile Number') {
      if (value === '') {
        updateField(field.label, value);
        return;
      }

      if (isNumeric(value)) {
        updateField(field.label, value);
      }
    } else {
      updateField(field.label, value);
    }
  };

  return (
    <AuthLayout smallLogo>
      <section className="programme-details">
        {programme && (
          <>
            {showConfirmation && (
              <section className={getClassNames('programme-details__confirmation', { pps })}>
                <h1 className={getClassNames('programme-details__confirmation__heading', { pps })}>
                  Order Confirmation
                </h1>
                <section
                  className={getClassNames('programme-details__confirmation__fields visible-scrollbar', { pps })}
                >
                  {fields.map(
                    (field: Field): ReactElement => (
                      <div
                        className={getClassNames('programme-details__confirmation__field', { pps })}
                        key={field.label}
                      >
                        <p className={getClassNames('programme-details__confirmation__field__label', { pps })}>
                          {field.label}:
                        </p>
                        <p className={getClassNames('programme-details__confirmation__field__value', { pps })}>
                          {renderConfirmationValue(field)}
                        </p>
                      </div>
                    )
                  )}
                </section>
              </section>
            )}

            <section className={getClassNames('programme-details__details', { pps })}>
              <p className={getClassNames('programme-details__details__line-one', { pps })}>You are joining</p>
              <div className={getClassNames('programme-details__details__line-two', { pps })}>
                <p className={getClassNames('programme-details__details__name', { pps })}>{programme.name}</p>
                <p className={getClassNames('programme-details__details__price', { pps })}>
                  R{programme.registrationRequirement.programmePrice}
                </p>
              </div>
              <p className={getClassNames('programme-details__details__line-three', { pps })}>
                {formatProgrammeDates(programme.fromDate, programme.toDate)}
              </p>
            </section>

            {!showConfirmation && (
              <section className={getClassNames('programme-details__fields visible-scrollbar', { pps })}>
                {fields.map(
                  (field: Field): ReactElement => (
                    <div key={field.label} className={getClassNames('programme-details__field', { pps })}>
                      <div className={getClassNames('programme-details__field__label-container', { pps })}>
                        <label
                          className={getClassNames('programme-details__field__label', { pps })}
                          htmlFor={field.label}
                        >
                          {field.label}:
                        </label>
                        {field.optional && (
                          <p className={getClassNames('programme-details__field__optional-text', { pps })}>
                            (Optional)
                          </p>
                        )}
                      </div>
                      {field.type === 'text' && (
                        <input
                          className={getClassNames('programme-details__field__text-input', { pps })}
                          type="text"
                          id={field.label}
                          name={field.label}
                          value={field.value}
                          onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
                            handleFieldChange(field, target.value)
                          }
                        />
                      )}
                      {field.type === 'checkbox' && (
                        <Checkbox
                          value={!!field.value}
                          onChange={(value: boolean): void => updateField(field.label, value)}
                        />
                      )}
                    </div>
                  )
                )}
              </section>
            )}
            <section className={getClassNames('programme-details__buttons', { confirmation: showConfirmation, pps })}>
              {showConfirmation ? (
                <Button text="Proceed to Payment" onClick={onProceedToPaymentClick} auth={!pps} />
              ) : (
                <Button text="Continue" onClick={handleContinueClick} auth={!pps} />
              )}
            </section>
          </>
        )}
      </section>
    </AuthLayout>
  );
};

export default ProgrammeDetails;
